'use client'

import Button from '@/components/design-system/button'
import CheckIcon from '@/components/icons/check'
import { api } from '@/services'
import type { NotificationType } from '@/types/notification'
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslations } from 'next-intl'

export default function NotificationsReadAllButton({
  type,
}: {
  type: NotificationType
}) {
  const t = useTranslations('notifications')

  const queryClient = useQueryClient()

  const { mutate } = useMutation(
    async () => {
      const { data } = await api.post('/user/notifications/mark-as-read', {
        type,
      })
      return data
    },
    {
      onMutate: () => {
        queryClient.setQueryData<
          InfiniteData<{
            items: Notification[]
            totalItems: number
            nextCursor: number | null
          }>
        >(['notifications', type], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                items: page.items.map((item) => {
                  return {
                    ...item,
                    readMarked: true,
                  }
                }),
              })),
            }
          }
        })

        queryClient.setQueryData<{
          activity: number
          alert: number
        }>(['notifications-count'], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              [type]: 0,
            }
          }
        })
      },
    }
  )

  const handleClick = () => {
    mutate()
  }

  return (
    <Button
      color='white'
      onClick={handleClick}
      size='sm'
      startIcon={<CheckIcon size='sm' color='primary' />}
      variant='transparent'
    >
      {t('mark-all-read')}
    </Button>
  )
}
